import { z } from 'zod';
import { AuthRefreshFailedError, type ErrorEnvelope } from './errors.js';
import { ApiError } from './errors.js';

// W-6: Zod schema for the refresh response — asserts shape before storing tokens.
const RefreshResponseSchema = z.object({
  sessionToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';
const EMBED_ORIGIN_HEADER = 'X-Embed-Origin';

let _tokenGetter: (() => string | null) | null = null;
let _tokenSetter: ((sessionToken: string, refreshToken: string) => void) | null = null;
let _refreshTokenGetter: (() => string | null) | null = null;
let _tokenClearer: (() => void) | null = null;
let _isEmbedded = false;
let _embedOrigin: string | null = null;

let _refreshInFlight: Promise<void> | null = null;

export function configureApiClient(opts: {
  tokenGetter: () => string | null;
  refreshTokenGetter?: () => string | null;
  tokenSetter?: (sessionToken: string, refreshToken: string) => void;
  tokenClearer?: () => void;
  isEmbedded?: boolean;
  embedOrigin?: string;
}): void {
  _tokenGetter = opts.tokenGetter;
  _refreshTokenGetter = opts.refreshTokenGetter ?? null;
  _tokenSetter = opts.tokenSetter ?? null;
  _tokenClearer = opts.tokenClearer ?? null;
  _isEmbedded = opts.isEmbedded ?? false;
  _embedOrigin = opts.embedOrigin ?? null;
}

function buildHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers);

  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');

  const token = _tokenGetter?.();
  if (token !== null && token !== undefined) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (_isEmbedded && _embedOrigin !== null) {
    headers.set(EMBED_ORIGIN_HEADER, _embedOrigin);
  }

  return headers;
}

async function doRefresh(): Promise<void> {
  const refreshToken = _refreshTokenGetter?.() ?? null;
  if (refreshToken === null) {
    _tokenClearer?.();
    throw new AuthRefreshFailedError();
  }

  try {
    const url = `${API_BASE_URL}/auth/refresh`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      _tokenClearer?.();
      throw new AuthRefreshFailedError();
    }

    // W-6: validate shape with Zod before storing — prevents storing "undefined" strings.
    const raw = (await response.json()) as unknown;
    const parsed = RefreshResponseSchema.safeParse(raw);
    if (!parsed.success) {
      _tokenClearer?.();
      throw new AuthRefreshFailedError();
    }
    _tokenSetter?.(parsed.data.sessionToken, parsed.data.refreshToken);
  } catch (err) {
    if (err instanceof AuthRefreshFailedError) throw err;
    _tokenClearer?.();
    throw new AuthRefreshFailedError();
  }
}

async function refreshOnce(): Promise<void> {
  if (_refreshInFlight !== null) {
    return _refreshInFlight;
  }

  _refreshInFlight = doRefresh().finally(() => {
    _refreshInFlight = null;
  });

  return _refreshInFlight;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const headers = buildHeaders(init);

  const response = await fetch(url, { ...init, headers });

  const requestId = response.headers.get('X-Request-Id') ?? undefined;

  if (response.status === 401 && path !== '/auth/refresh') {
    let envelope: ErrorEnvelope | null = null;
    try {
      const body = (await response.json()) as Record<string, unknown>;
      if (
        typeof body.error === 'object' &&
        body.error !== null &&
        'code' in (body.error as object)
      ) {
        envelope = body as unknown as ErrorEnvelope;
      } else if (typeof body.code === 'string') {
        envelope = {
          error: {
            code: body.code as ErrorEnvelope['error']['code'],
            message: typeof body.error === 'string' ? body.error : 'Unauthorized',
          },
        };
      }
    } catch {
      // ignore parse error
    }

    // W-7: only refresh when the backend explicitly signals SESSION_EXPIRED / SESSION_INVALID.
    // A 401 with no parseable envelope (CDN/WAF/proxy) must bubble up — never silently refresh.
    const code = envelope?.error.code;
    if (code === 'SESSION_EXPIRED' || code === 'SESSION_INVALID') {
      await refreshOnce();

      const retryHeaders = buildHeaders(init);
      const retryResponse = await fetch(url, { ...init, headers: retryHeaders });

      if (!retryResponse.ok) {
        let retryEnvelope: ErrorEnvelope | null = null;
        try {
          retryEnvelope = (await retryResponse.json()) as ErrorEnvelope;
        } catch {
          throw new ApiError(
            `HTTP ${retryResponse.status}: ${retryResponse.statusText}`,
            'UNKNOWN',
            retryResponse.status,
            undefined,
            requestId,
          );
        }

        const resolvedRequestId = retryEnvelope.error.request_id ?? requestId;
        const errorPayload =
          resolvedRequestId !== undefined
            ? { ...retryEnvelope.error, request_id: resolvedRequestId }
            : retryEnvelope.error;
        throw ApiError.fromEnvelope({ error: errorPayload }, retryResponse.status);
      }

      if (retryResponse.status === 204) {
        return undefined as unknown as T;
      }

      return (await retryResponse.json()) as T;
    }

    if (envelope !== null) {
      const resolvedRequestId = envelope.error.request_id ?? requestId;
      const errorPayload =
        resolvedRequestId !== undefined
          ? { ...envelope.error, request_id: resolvedRequestId }
          : envelope.error;
      throw ApiError.fromEnvelope({ error: errorPayload }, response.status);
    }

    // W-7: 401 with no parseable envelope (CDN/WAF/proxy) — bubble up as UNKNOWN.
    throw new ApiError('HTTP 401: Unauthorized', 'UNKNOWN', 401, undefined, requestId);
  }

  if (!response.ok) {
    let envelope: ErrorEnvelope | null = null;

    try {
      envelope = (await response.json()) as ErrorEnvelope;
    } catch {
      throw new ApiError(
        `HTTP ${response.status}: ${response.statusText}`,
        'UNKNOWN',
        response.status,
        undefined,
        requestId,
      );
    }

    const resolvedRequestId = envelope.error.request_id ?? requestId;
    const errorPayload =
      resolvedRequestId !== undefined
        ? { ...envelope.error, request_id: resolvedRequestId }
        : envelope.error;
    throw ApiError.fromEnvelope({ error: errorPayload }, response.status);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return (await response.json()) as T;
}

export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}
